import { schoolsAPI, organizationsAPI, invitationsAPI, usersAPI } from '../../api';
import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { WORLDWIDE_TIMEZONES, getAllTimezones, formatTimezoneDisplay } from '../../utils/worldwideTimezones';
import EditUserModal from './EditUserModal';

export default function AdminPanel() {
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const { currentOrganization: organization, refreshSchools } = useTenant();
  const {
    user,
    permissions,
    canCreateSchool,
    canDeleteSchool,
    canInviteUsers,
    canManageSpecificUser,
    canDeleteOrganization,
    getAvailableInviteRoles
  } = useUserPermissions();
  
  // Get initial tab from URL hash or default to 'users'
  const getInitialTab = () => {
    const hash = location.hash.slice(1); // Remove the # symbol
    const validTabs = ['invitations', 'users', 'schools', 'organization'];
    return validTabs.includes(hash) ? hash : 'users';
  };

  // Update active tab options to include 'organization' and 'schools'
  const [activeTab, setActiveTab] = useState(getInitialTab());

  // Debug log to check if organization is received
  console.log('🔍 AdminPanel: organization from TenantContext:', organization);
  console.log('🔍 AdminPanel: user permissions:', { user, permissions });
  
  // Update active tab options to include 'organization' and 'schools'
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [supportedTimezones, setSupportedTimezones] = useState([]);
  const [editingSchoolTimezone, setEditingSchoolTimezone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSchoolDeleteConfirm, setShowSchoolDeleteConfirm] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  // User editing state
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Filter WORLDWIDE_TIMEZONES to only show the 24 supported timezones
  // These match the Account Settings page and backend's SUPPORTED_TIMEZONES list
  const getSupportedTimezones = () => {
    const supportedTimezoneValues = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'Pacific/Honolulu',
      'Pacific/Midway',
      'America/St_Johns',
      'America/Sao_Paulo',
      'Atlantic/South_Georgia',
      'Atlantic/Cape_Verde',
      'Europe/London',
      'Europe/Paris',
      'Europe/Athens',
      'Europe/Moscow',
      'Asia/Tehran',
      'Asia/Dubai',
      'Asia/Karachi',
      'Asia/Kolkata',
      'Asia/Dhaka',
      'Asia/Bangkok',
      'Asia/Shanghai',
      'Asia/Tokyo',
      'Australia/Sydney',
      'Pacific/Noumea',
      'Pacific/Auckland',
      'Pacific/Tongatapu',
      'Pacific/Kiritimati'
    ];

    // Filter WORLDWIDE_TIMEZONES to only include supported timezones
    return WORLDWIDE_TIMEZONES.map(regionGroup => ({
      ...regionGroup,
      timezones: regionGroup.timezones.filter(tz => 
        supportedTimezoneValues.includes(tz.value)
      )
    })).filter(regionGroup => regionGroup.timezones.length > 0); // Remove empty regions
  };

  const filteredTimezones = getSupportedTimezones();

  // Handle hash navigation
  useEffect(() => {
    const hash = location.hash.slice(1);
    const validTabs = ['invitations', 'users', 'schools', 'organization'];
    // Only update if there's a hash in the URL and it's different from current tab
    if (hash && validTabs.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash, activeTab]);

  // Function to change tab and update URL hash
  const changeTab = (tab) => {
    setActiveTab(tab);
    window.history.pushState(null, '', `#${tab}`);
  };

  // Form state for sending an invitation
  const [invitation, setInvitation] = useState({
    email: '',
    role: '',
    organization_id: '', // Make sure to set this based on the admin's organization
    inviteType: 'tenant', // 'tenant' or 'school'
    schoolId: ''
  });

  // Form state for creating a new school
  const [newSchool, setNewSchool] = useState({
    name: '',
    timezone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: ''
  });

  // Fetch invitations for the admin's organization
  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Log JWT token for debugging
      console.log('🔍 JWT Token from frontend:', token);
      
      // Decode and log JWT payload for debugging
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('🔍 Decoded JWT payload in frontend:', payload);
        console.log('🔍 Organization claim in token:', payload['https://arcattend.com/organization_id']);
      } catch (decodeError) {
        console.error('Failed to decode JWT token:', decodeError);
      }
      
      const data = await invitationsAPI.getInvitations(token);
      setInvitations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for this organization with retry logic
  const fetchUsers = async (retryCount = 0, maxRetries = 3) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      console.log(`Fetching users (attempt ${retryCount + 1})`);
      
      const data = await usersAPI.getUsers(token);
      console.log("Fetched users:", data);
      console.log("🔍 First user structure:", JSON.stringify(data[0], null, 2));
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      
      // Check if this is a network error or timing issue we should retry
      const isRetryableError = error.message.includes('USER_NOT_SYNCED') ||
                              error.message.includes('Failed to fetch users') ||
                              error.message.includes('Network request failed');
      
      // If we haven't exhausted retries for retryable errors, try again
      if (retryCount < maxRetries && isRetryableError) {
        console.log(`Retryable error, retrying in ${(retryCount + 1) * 1000}ms...`);
        setLoading(false);
        
        setTimeout(() => {
          fetchUsers(retryCount + 1, maxRetries);
        }, (retryCount + 1) * 1000);
        return;
      }
      
      // Show a user-friendly error message for timing issues
      if (error.message.includes('USER_NOT_SYNCED')) {
        setError('Your account is still being set up. Please refresh the page or try again in a moment.');
      } else {
        setError('Failed to load users. ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Set up invitation form with organization data when available
  useEffect(() => {
    if (organization) {
      setInvitation(prev => ({
        ...prev,
        organization_id: organization.id
      }));
    }
  }, [organization]);

  // Reset role and school when invite type changes
  const handleInviteTypeChange = (newType) => {
    setInvitation(prev => ({
      ...prev,
      inviteType: newType,
      role: '', // Reset role when changing invite type
      schoolId: newType === 'tenant' ? '' : prev.schoolId // Reset school if switching to tenant
    }));
  };

  // Get available roles based on invite type and user permissions
  const getAvailableRoles = () => {
    if (!organization) return [];
    
    // Get roles this user is allowed to invite based on their permissions
    const availableRoles = getAvailableInviteRoles();
    
    if (invitation.inviteType === 'tenant') {
      // For organization-wide invites, filter to organization-scoped roles
      return availableRoles.filter(role => 
        ['tenant_administrator', 'organization_admin', 'organization_teacher', 'organization_staff'].includes(role.value)
      );
    } else {
      // For school-specific invites, filter to school-scoped roles
      return availableRoles.filter(role => 
        ['school_admin', 'teacher', 'substitute', 'support_staff'].includes(role.value)
      );
    }
  };

  // Get all available roles for user editing (not limited by invite type)
  const getAllAvailableRoles = () => {
    if (!organization) return [];
    
    // For editing users, tenant admins can assign any role including their own level
    const allRoles = [
      { value: 'tenant_administrator', label: 'Tenant Administrator', level: 5 },
      { value: 'organization_admin', label: 'Organization Admin', level: 4 },
      { value: 'organization_teacher', label: 'Organization Teacher', level: 3 },
      { value: 'organization_staff', label: 'Organization Staff', level: 3 },
      { value: 'school_admin', label: 'School Admin', level: 2 },
      { value: 'teacher', label: 'Teacher', level: 1 },
    ];
    
    // Get current user's role level
    const getCurrentUserLevel = () => {
      switch (user.role) {
        case 'tenant_administrator': return 5;
        case 'organization_admin': return 4;
        case 'organization_teacher': return 3;
        case 'organization_staff': return 3;
        case 'school_admin': return 2;
        case 'teacher': return 1;
        default: return 0;
      }
    };
    
    const currentUserLevel = getCurrentUserLevel();
    
    // For editing users, allow same level or below (tenant admins can assign tenant admin)
    const availableRoles = allRoles.filter(role => {
      // Can assign roles at same level or below
      if (role.level > currentUserLevel) {
        return false;
      }
      
      // Special cases for role-specific restrictions
      if (user.role === 'school_admin') {
        // School admins can assign school admin and teacher roles
        return role.value === 'school_admin' || role.value === 'teacher';
      }
      
      if (user.role === 'teacher') {
        // Teachers cannot assign roles
        return false;
      }
      
      return true;
    });
    
    // Sort by level descending (higher roles first)
    return availableRoles.sort((a, b) => b.level - a.level);
  };

  // Fetch schools from the backend attendance service
  const fetchSchools = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Only fetch schools if we have an organization ID
      if (!organization || !organization.id) {
        console.log('No organization found, cannot fetch schools');
        setSchools([]);
        return;
      }
      
      // Try to fetch schools using JWT token (new approach)
      // If it fails due to missing organization claims, try refreshing token
      try {
        const data = await schoolsAPI.getSchools(token);
        setSchools(data);
      } catch (schoolsError) {
        console.warn('Failed to fetch schools with JWT approach:', schoolsError.message);
        
        // If "No organization information found", try refreshing token
        if (schoolsError.message.includes('No organization information found')) {
          console.log('🔄 AdminPanel: Token may be stale, refreshing and retrying schools...');
          try {
            const freshToken = await getAccessTokenSilently({ cacheMode: 'off' });
            const data = await schoolsAPI.getSchools(freshToken);
            setSchools(data);
            console.log('✅ AdminPanel: Schools loaded after token refresh');
          } catch (retryError) {
            console.error('❌ AdminPanel: Still failed after token refresh:', retryError);
            setSchools([]);
            setError('JWT tokens not yet configured with organization claims. Please complete Auth0 Action setup.');
          }
        } else {
          setSchools([]);
          setError(`Failed to fetch schools: ${schoolsError.message}`);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create a new school
  const createSchool = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Make sure we have an organization ID
      if (!organization || !organization.id) {
        throw new Error('Organization information is required to create a school');
      }
      
      // Include organization_id in the request body
      const schoolData = {
        ...newSchool,
        organization_id: organization.id
      };
      
      try {
        await schoolsAPI.createSchool(schoolData, token);
        
        setSuccess('School created successfully');
        setNewSchool({
          name: '',
          timezone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          phoneNumber: ''
        });
        fetchSchools(); // Refresh the schools list
        try {
          await refreshSchools(); // Update TenantContext and sidebar
        } catch (refreshError) {
          console.warn('Failed to refresh sidebar schools:', refreshError);
        }
      } catch (schoolError) {
        if (schoolError.message.includes('No organization information found')) {
          console.log('🔄 AdminPanel: Token may be stale for school creation, refreshing and retrying...');
          try {
            const freshToken = await getAccessTokenSilently({ cacheMode: 'off' });
            await schoolsAPI.createSchool(schoolData, freshToken);
            setSuccess('School created successfully');
            setNewSchool({
              name: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
              phoneNumber: ''
            });
            fetchSchools(); // Refresh the schools list
            try {
              await refreshSchools(); // Update TenantContext and sidebar
            } catch (refreshError) {
              console.warn('Failed to refresh sidebar schools:', refreshError);
            }
            console.log('✅ AdminPanel: School created after token refresh');
          } catch (retryError) {
            console.error('❌ AdminPanel: School creation still failed after token refresh:', retryError);
            setError('JWT tokens not yet configured with organization claims. Please complete Auth0 Action setup to create schools.');
          }
        } else {
          throw schoolError; // Re-throw other errors
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete a school - show confirmation overlay
  const initiateSchoolDeletion = (school) => {
    setSchoolToDelete(school);
    setDeleteConfirmationText('');
    setShowSchoolDeleteConfirm(true);
  };

  // Confirm and execute school deletion
  const confirmSchoolDeletion = async () => {
    if (!schoolToDelete || deleteConfirmationText !== schoolToDelete.name) {
      setError('School name does not match. Please type the exact school name to confirm.');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await schoolsAPI.deleteSchool(schoolToDelete.id, token);
      
      setSuccess('School permanently deleted');
      setShowSchoolDeleteConfirm(false);
      setSchoolToDelete(null);
      setDeleteConfirmationText('');
      fetchSchools(); // Refresh the schools list
      try {
        await refreshSchools(); // Update TenantContext and sidebar
      } catch (refreshError) {
        console.warn('Failed to refresh sidebar schools:', refreshError);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cancel school deletion
  const cancelSchoolDeletion = () => {
    setShowSchoolDeleteConfirm(false);
    setSchoolToDelete(null);
    setDeleteConfirmationText('');
  };

  // Fetch supported timezones
  const fetchSupportedTimezones = async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await schoolsAPI.getSupportedTimezones(token);
      setSupportedTimezones(data.timezones || []);
    } catch (err) {
      console.warn('Failed to fetch supported timezones:', err.message);
      // Use local comprehensive timezone list as fallback
      setSupportedTimezones(getAllTimezones());
    }
  };

  // Update school timezone
  const updateSchoolTimezone = async (schoolId, timezone) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await schoolsAPI.updateSchoolTimezone(schoolId, timezone, token);
      
      setSuccess('School timezone updated successfully');
      setEditingSchoolTimezone(null);
      fetchSchools(); // Refresh the schools list
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cancel timezone editing
  const cancelTimezoneEdit = () => {
    setEditingSchoolTimezone(null);
  };

  // Send an invitation to a new user
  const sendInvitation = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Prepare invitation data
      const invitationData = {
        email: invitation.email,
        role: invitation.role,
        organization_id: invitation.organization_id,
        inviteType: invitation.inviteType
      };

      // Add school ID if this is a school-specific invitation
      if (invitation.inviteType === 'school' && invitation.schoolId) {
        invitationData.schoolId = invitation.schoolId;
      }
      
      await invitationsAPI.sendInvitation(invitationData, token);
      
      setSuccess('Invitation sent successfully');
      
      // Reset form while preserving organization selection
      setInvitation({ 
        email: '',
        role: '',
        organization_id: invitation.organization_id,
        inviteType: 'tenant',
        schoolId: ''
      });
      
      fetchInvitations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteInvitation = async (invId) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) return;
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await invitationsAPI.deleteInvitation(invId, token);
      
      setSuccess('Invitation deleted successfully');
      fetchInvitations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendInvitation = async (invId) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await invitationsAPI.resendInvitation(invId, token);
      
      setSuccess('Invitation resent successfully');
      fetchInvitations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove user from organization
  const removeUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from the organization? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await usersAPI.removeUser(userId, token);
      
      setSuccess(`${userName} has been removed from the organization successfully`);
      fetchUsers(); // Refresh the users list
    } catch (err) {
      setError(`Failed to remove user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Edit user - show modal
  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditUserModal(true);
  };

  // Update user information
  const handleUpdateUser = async (updateData) => {
    if (!editingUser) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      await usersAPI.updateUser(editingUser.id, updateData, token);
      
      setSuccess(`${updateData.name} has been updated successfully`);
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers(); // Refresh the users list
    } catch (err) {
      setError(`Failed to update user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Close edit user modal
  const handleCloseEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  // Add function to delete the organization
  const deleteOrganization = async () => {
    if (!organization) {
      setError('No organization found to delete');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessTokenSilently();
      
      await organizationsAPI.deleteOrganization(organization.id, token);
      
      setSuccess('Organization deleted successfully. You will be redirected shortly.');
      setShowDeleteConfirm(false);
      
      // After successful deletion, redirect to a dedicated post-deletion page
      setTimeout(() => {
        window.location.href = '/organization-deleted';
      }, 2000);
      
    } catch (err) {
      setError(`Failed to delete organization: ${err.message}`);
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  // Clear notifications after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Helper function to format user scope display
  const formatUserScope = (user) => {
    // Check if accessible_school_ids exists and has values (backend uses snake_case)
    const schoolIds = user.accessible_school_ids || user.accessibleSchoolIds || [];
    if (schoolIds.length === 0) {
      return (
        <div>
          <div className="font-medium text-slate-700">All Schools</div>
          <div className="text-xs text-slate-500">Organization-wide access</div>
        </div>
      );
    }

    // Filter out deleted schools and only show schools that still exist
    const validSchoolIds = schoolIds.filter(schoolId => 
      schools.find(s => s.id === schoolId)
    );

    const validSchoolNames = validSchoolIds.map(schoolId => {
      const school = schools.find(s => s.id === schoolId);
      return school.name;
    });

    // If no valid schools remain, show as organization-wide access
    if (validSchoolNames.length === 0) {
      return (
        <div>
          <div className="font-medium text-slate-700">All Schools</div>
          <div className="text-xs text-slate-500">No valid school assignments</div>
        </div>
      );
    }

    return (
      <div>
        <div className="font-medium text-slate-700">Specific Schools</div>
        <div className="text-xs text-slate-500" title={validSchoolNames.join(', ')}>
          {validSchoolNames.length === 1 
            ? validSchoolNames[0]
            : `${validSchoolNames[0]}${validSchoolNames.length > 1 ? ` +${validSchoolNames.length - 1} more` : ''}`
          }
          {/* Note: Removed "X removed" indicator as it was misleading - shows when schools exist in DB but not in user's filtered view */}
        </div>
      </div>
    );
  };

  // Update useEffect to fetch organization details
  useEffect(() => {
    const initializePanel = async () => {
      // Test token refresh to see if it gets organization claims
      try {
        console.log('🔄 Testing fresh token fetch...');
        const freshToken = await getAccessTokenSilently({ 
          cacheMode: 'off',
          ignoreCache: true 
        });
        
        console.log('🔍 Fresh JWT Token:', freshToken);
        
        // Decode and log JWT payload for debugging
        try {
          const payload = JSON.parse(atob(freshToken.split('.')[1]));
          console.log('🔍 Fresh JWT payload:', payload);
          console.log('🔍 Organization claim in fresh token:', payload['https://arcattend.com/organization_id']);
        } catch (decodeError) {
          console.error('Failed to decode fresh JWT token:', decodeError);
        }
      } catch (error) {
        console.error('Failed to get fresh token:', error);
      }
    };
    
    initializePanel();
    fetchInvitations();
    fetchUsers();
    // Don't call fetchSchools here - it needs organization data first
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch schools when organization is available
  useEffect(() => {
    if (organization && organization.id) {
      fetchSchools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Set default tab based on user permissions (only on initial load)
  const [hasSetInitialTab, setHasSetInitialTab] = useState(false);
  
  useEffect(() => {
    if (user && permissions && !hasSetInitialTab) {
      if (canInviteUsers()) {
        setActiveTab('invitations');
      } else {
        setActiveTab('users'); // Fallback to users tab
      }
      setHasSetInitialTab(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, permissions, hasSetInitialTab]);

  // Load supported timezones when schools tab is active
  useEffect(() => {
    if (activeTab === 'schools' && supportedTimezones.length === 0) {
      fetchSupportedTimezones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Update the tab navigation to include the Organization tab
  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800">Admin Panel</h2>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Role:</span> {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Loading...'}
        </div>
      </div>
      
      {/* Tab Navigation - Updated with new Organization tab */}
      <div className="flex border-b mb-6">
        {canInviteUsers() && (
          <button
            onClick={() => changeTab('invitations')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'invitations' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Invitations
          </button>
        )}
        <button
          onClick={() => changeTab('users')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          User Management
        </button>
        <button
          onClick={() => changeTab('schools')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'schools' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Schools
        </button>
        <button
          onClick={() => changeTab('organization')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'organization' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Organization
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-700">
          <p>{success}</p>
        </div>
      )}
      
      {activeTab === 'invitations' && !canInviteUsers() && (
        <div className="text-center py-8">
          <div className="text-gray-600 text-lg mb-4">
            Access Denied
          </div>
          <div className="text-gray-500 text-sm">
            You don't have permission to manage invitations. Contact your administrator if you need access.
          </div>
        </div>
      )}

      {activeTab === 'invitations' && canInviteUsers() && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Send New Invitation</h3>
          <form onSubmit={sendInvitation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={invitation.email}
                  onChange={(e) =>
                    setInvitation({ ...invitation, email: e.target.value })
                  }
                  required
                  className="w-full border rounded px-3 py-2"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Organization</label>
                <input
                  type="text"
                  value={organization?.name || 'Loading...'}
                  disabled
                  className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-600 cursor-not-allowed"
                  placeholder="Organization name"
                />
                <p className="text-xs text-gray-500 mt-1">Users will be invited to your organization</p>
              </div>
            </div>

            {/* Invite Type Selection - Only show when organization is available */}
            {organization && (
              <div>
                <label className="block text-sm font-medium mb-2">Invitation Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="inviteType"
                      value="tenant"
                      checked={invitation.inviteType === 'tenant'}
                      onChange={(e) => handleInviteTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Organization-wide Access</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="inviteType"
                      value="school"
                      checked={invitation.inviteType === 'school'}
                      onChange={(e) => handleInviteTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Specific School Access</span>
                  </label>
                </div>
              </div>
            )}

            {/* School Selection - Only show when school invite type is selected */}
            {organization && invitation.inviteType === 'school' && (
              <div>
                <label className="block text-sm font-medium mb-1">School</label>
                <select
                  value={invitation.schoolId}
                  onChange={(e) =>
                    setInvitation({ ...invitation, schoolId: e.target.value })
                  }
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select School</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
                {schools.length === 0 && (
                  <p className="text-sm text-slate-500 mt-1">
                    No schools available. Create schools in the Schools tab first.
                  </p>
                )}
              </div>
            )}

            {/* Role Selection - Only show when organization is available */}
            {organization && (
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={invitation.role}
                  onChange={(e) =>
                    setInvitation({ ...invitation, role: e.target.value })
                  }
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Role</option>
                  {getAvailableRoles().map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {invitation.inviteType === 'tenant' && (
                  <p className="text-sm text-slate-500 mt-1">
                    Organization Admin and Staff roles have access to all schools within the organization.
                  </p>
                )}
                {invitation.inviteType === 'school' && invitation.schoolId && (
                  <p className="text-sm text-slate-500 mt-1">
                    User will have access to {schools.find(s => s.id === invitation.schoolId)?.name} only.
                  </p>
                )}
              </div>
            )}

            <div>
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={loading || !organization || !invitation.role || (invitation.inviteType === 'school' && !invitation.schoolId)}
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
          <h3 className="text-lg font-semibold mt-8 mb-4">Sent Invitations</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-2 text-left font-semibold">Email</th>
                  <th className="p-2 text-left font-semibold">Role</th>
                  <th className="p-2 text-left font-semibold">Access Level</th>
                  <th className="p-2 text-left font-semibold">Status</th>
                  <th className="p-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-4 text-center text-slate-500">
                      No invitations sent
                    </td>
                  </tr>
                ) : (
                  invitations.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-slate-50">
                      <td className="p-2">{inv.email}</td>
                      <td className="p-2">
                        <span className="capitalize">
                          {inv.role?.replace('_', ' ') || 'N/A'}
                        </span>
                      </td>
                      <td className="p-2">
                        {(() => {
                          // School-scoped roles: school_admin, teacher, substitute, support_staff
                          const schoolScopedRoles = ['school_admin', 'teacher', 'substitute', 'support_staff'];
                          const isSchoolRole = schoolScopedRoles.includes(inv.role);
                          
                          if (isSchoolRole && inv.school) {
                            return (
                              <div>
                                <div className="font-medium">School Access</div>
                                <div className="text-xs text-slate-500">
                                  {inv.school.name}
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div>
                                <div className="font-medium">Organization Access</div>
                                <div className="text-xs text-slate-500">All schools</div>
                              </div>
                            );
                          }
                        })()}
                      </td>
                      <td className="p-2">
                        {inv.used
                          ? 'Accepted'
                          : new Date() > new Date(inv.expiresAt)
                          ? 'Expired'
                          : 'Pending'}
                      </td>
                      <td className="p-2 text-right">
                        {!inv.used && new Date() <= new Date(inv.expiresAt) && (
                          <button
                            onClick={() => resendInvitation(inv.id)}
                            className="text-blue-600 hover:underline mr-2"
                          >
                            Resend
                          </button>
                        )}
                        <button
                          onClick={() => deleteInvitation(inv.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">User Management</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchUsers}
                className="px-3 py-1 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 text-sm flex items-center"
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Name</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Email</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Role</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Scope</th>
                  <th className="py-3 px-4 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-4 text-center text-slate-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-700 flex items-center gap-2">
                        {user.name}
                      </td>
                      <td className="py-3 px-4">{user.email}</td>
                      <td className="py-3 px-4">{user.role}</td>
                      <td className="py-3 px-4">
                        {formatUserScope(user)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {canManageSpecificUser(user) ? (
                          <>
                            <button 
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:underline mr-2"
                              disabled={loading}
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => removeUser(user.id, user.name || user.email)}
                              className="text-red-600 hover:underline"
                              disabled={loading}
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm">No Access</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schools Management tab */}
      {activeTab === 'schools' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">School Management</h3>
            <button 
              onClick={fetchSchools}
              className="px-3 py-1 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh
            </button>
          </div>

          {/* Add New School Form */}
          {canCreateSchool() && (
            <div className="bg-slate-50 p-4 rounded-lg mb-6">
              <h4 className="text-md font-semibold mb-4">Add New School</h4>
              {!organization ? (
                <div className="text-center py-4">
                  <p className="text-slate-500">Loading organization information...</p>
                </div>
              ) : (
                <form onSubmit={createSchool} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">School Name *</label>
                    <input
                      type="text"
                      value={newSchool.name}
                      onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                      required
                      className="w-full border rounded px-3 py-2"
                      placeholder="Enter school name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Timezone *</label>
                    <select
                      value={newSchool.timezone || ''}
                      onChange={(e) => setNewSchool({ ...newSchool, timezone: e.target.value })}
                      required
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="">Select timezone...</option>
                      {filteredTimezones.map((regionGroup) => (
                        <optgroup key={regionGroup.region} label={regionGroup.region}>
                          {regionGroup.timezones.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      This timezone will be used for all attendance data and reports for this school.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={newSchool.phoneNumber}
                      onChange={(e) => setNewSchool({ ...newSchool, phoneNumber: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <input
                      type="text"
                      value={newSchool.address}
                      onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input
                      type="text"
                      value={newSchool.city}
                      onChange={(e) => setNewSchool({ ...newSchool, city: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <input
                      type="text"
                      value={newSchool.state}
                      onChange={(e) => setNewSchool({ ...newSchool, state: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP Code</label>
                    <input
                      type="text"
                      value={newSchool.zipCode}
                      onChange={(e) => setNewSchool({ ...newSchool, zipCode: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="12345"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={loading || !organization}
                    >
                      {loading ? 'Creating...' : 'Create School'}
                    </button>
                    {organization && (
                      <p className="text-sm text-slate-500 mt-2">
                        School will be created for organization: <span className="font-medium">{organization.name}</span>
                      </p>
                    )}
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Schools List */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Name</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Address</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Phone</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Timezone</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-500">Created</th>
                  <th className="py-3 px-4 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schools.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-4 text-center text-slate-500">
                      No schools found
                    </td>
                  </tr>
                ) : (
                  schools.map((school) => (
                    <tr key={school.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-700">{school.name}</td>
                      <td className="py-3 px-4 text-slate-600">
                        {school.address && (
                          <div>
                            <div>{school.address}</div>
                            {(school.city || school.state || school.zipCode) && (
                              <div className="text-sm text-slate-500">
                                {[school.city, school.state, school.zipCode].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{school.phoneNumber || '-'}</td>
                      <td className="py-3 px-4 text-slate-600">
                        {editingSchoolTimezone === school.id ? (
                          <div className="flex items-center space-x-2">
                            <select
                              className="text-sm border rounded px-2 py-1 w-48"
                              defaultValue={school.timezone || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  updateSchoolTimezone(school.id, e.target.value);
                                }
                              }}
                            >
                              <option value="">Select timezone...</option>
                              {filteredTimezones.map((regionGroup) => (
                                <optgroup key={regionGroup.region} label={regionGroup.region}>
                                  {regionGroup.timezones.map((tz) => (
                                    <option key={tz.value} value={tz.value}>
                                      {tz.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                            <button
                              onClick={cancelTimezoneEdit}
                              className="text-gray-500 hover:text-gray-700 text-xs"
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm">
                              {school.timezone ? (
                                formatTimezoneDisplay(school.timezone)
                              ) : (
                                <span className="text-slate-400">Not set</span>
                              )}
                            </span>
                            <button
                              onClick={() => setEditingSchoolTimezone(school.id)}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                              title="Edit timezone"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {school.created_at ? new Date(school.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {canDeleteSchool() && (
                          <button 
                            onClick={() => initiateSchoolDeletion(school)}
                            className="text-red-600 hover:underline"
                            disabled={loading}
                          >
                            Delete
                          </button>
                        )}
                        {!canDeleteSchool() && (
                          <span className="text-gray-400 text-sm">No permission</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Organization Management tab */}
      {activeTab === 'organization' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Organization Management</h3>
          
          {organization ? (
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold mb-2">Organization Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{organization.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">ID</p>
                    <p className="font-medium">{organization.id}</p>
                  </div>
                  {organization.unique_id && (
                    <div>
                      <p className="text-sm text-slate-500">Unique ID</p>
                      <p className="font-medium">{organization.unique_id}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="font-medium">
                      {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold text-red-600 mb-2">Danger Zone</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Deleting your organization will permanently remove all associated data including classes, 
                  attendance records, and user associations. This action cannot be undone.
                </p>
                
                {canDeleteOrganization() ? (
                  !showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Delete Organization
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 p-4 rounded">
                      <p className="font-medium text-red-800 mb-4">
                        Are you absolutely sure you want to delete "{organization.name}"?
                      </p>
                      <p className="text-sm text-red-600 mb-4">
                        This will permanently delete all classes, attendance records, and user memberships. This action cannot be undone.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={deleteOrganization}
                          disabled={loading}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {loading ? 'Deleting...' : 'Yes, Delete Organization'}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={loading}
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded">
                    <p className="text-sm text-gray-600">
                      You don't have permission to delete this organization. Only Tenant Administrators can perform this action.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : loading ? (
            <p className="text-center text-slate-500">Loading organization details...</p>
          ) : (
            <p className="text-center text-slate-500">No organization found. You may need to create one.</p>
          )}
        </div>
      )}
    </div>

    {/* School Deletion Confirmation Overlay */}
    {showSchoolDeleteConfirm && schoolToDelete && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold text-red-600 mb-4">
            Permanently Delete School
          </h3>
          
          <div className="mb-4">
            <p className="text-sm text-slate-600 mb-2">
              You are about to permanently delete:
            </p>
            <p className="font-medium text-slate-800 bg-slate-100 p-2 rounded">
              {schoolToDelete.name}
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 p-3 rounded mb-4">
            <p className="text-sm text-red-700 font-medium mb-1">
              ⚠️ This action cannot be undone!
            </p>
            <p className="text-sm text-red-600">
              This will permanently delete the school and all associated categories, subjects, 
              classrooms, and attendance records. This data cannot be recovered.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              To confirm deletion, type the school name exactly as shown above:
            </label>
            <input
              type="text"
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="Type school name here"
              className="w-full border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={confirmSchoolDeletion}
              disabled={loading || deleteConfirmationText !== schoolToDelete.name}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Permanently Delete School'}
            </button>
            <button
              onClick={cancelSchoolDeletion}
              disabled={loading}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Edit User Modal */}
    {showEditUserModal && editingUser && (
      <EditUserModal
        onClose={handleCloseEditUserModal}
        onUpdate={handleUpdateUser}
        editingUser={editingUser}
        availableRoles={getAllAvailableRoles()}
        schools={schools}
        loading={loading}
      />
    )}

    </>
  );
}